import { useId } from 'react';
import { claveDeCliente } from '@/domain/orders';

/**
 * Campo para escribir el nombre del cliente, con los que ya existen a la mano.
 *
 * POR QUE NO SE REUSA EL BUSCADOR DE PLATILLOS
 *
 * Se parecen pero no son lo mismo. Las recetas son un catalogo cerrado: si el
 * texto no corresponde a una receta, no hay eleccion. Los clientes no tienen
 * catalogo, se escriben a mano en cada pedido, asi que aqui SIEMPRE hay que poder
 * escribir a alguien nuevo. Un campo que exija elegir de la lista dejaria a la
 * usuaria sin poder anotar un pedido de una persona que llega por primera vez.
 *
 * QUE APORTA SOBRE UN CAMPO DE TEXTO NORMAL
 *
 * El anterior era un campo de texto con la lista nativa del navegador, la misma
 * que ya habia dado problemas en el telefono con los platillos: a veces no
 * aparece. Aqui las coincidencias son botones, que siempre se pueden tocar.
 *
 * Y sobre todo avisa cuando el nombre escrito es alguien que YA existe con otra
 * escritura. Escribir "juan" teniendo a "Juan" no crea una persona nueva, pero
 * antes tampoco se veia; ahora lo dice y ofrece un toque para unificarlo. Las
 * cuentas los juntan igual, asi que esto es comodidad y claridad, no correccion:
 * la correccion vive en claveDeCliente, que es la que manda.
 */

interface Props {
  valor: string;
  onCambio: (valor: string) => void;
  /** Clientes ya usados, con la escritura que se muestra. Sin repetidos. */
  clientes: readonly string[];
  etiqueta?: string;
  autoFocus?: boolean;
  /** Cuantas coincidencias se ofrecen como botones. */
  maximo?: number;
}

export function CampoCliente({
  valor,
  onCambio,
  clientes,
  etiqueta = 'Cliente',
  autoFocus = false,
  maximo = 6,
}: Props) {
  const idCampo = useId();
  const idLista = useId();

  const buscado = claveDeCliente(valor);

  /** Por trozo del nombre, no solo por como empieza: "ann" encuentra "Susanna". */
  const coincidencias =
    buscado === ''
      ? []
      : clientes.filter((c) => claveDeCliente(c).includes(buscado));

  /** El mismo cliente escrito distinto. Es el caso que se quiere evitar. */
  const mismoOtraEscritura = clientes.find(
    (c) => claveDeCliente(c) === buscado && c !== valor.trim(),
  );

  /** Ya escrito igualito a uno que existe: nada que avisar. */
  const yaExacto = clientes.some((c) => c === valor.trim());

  const esNuevo = buscado !== '' && !clientes.some((c) => claveDeCliente(c) === buscado);

  /** Los que se ofrecen para tocar, sin repetir el que ya esta escrito. */
  const paraTocar = coincidencias.filter((c) => c !== valor.trim());

  return (
    <div>
      <label htmlFor={idCampo} className="block text-sm font-medium text-texto">
        {etiqueta}
      </label>

      <input
        id={idCampo}
        type="text"
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        list={idLista}
        autoComplete="off"
        autoCapitalize="words"
        maxLength={80}
        autoFocus={autoFocus}
        placeholder="Escribe el nombre"
        className="mt-1 min-h-11 w-full rounded-xl border border-borde-fuerte bg-superficie px-3 py-2 text-base text-texto"
      />
      <datalist id={idLista}>
        {clientes.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {/* El aviso que evita crear otra variante del mismo nombre. */}
      {mismoOtraEscritura !== undefined && (
        <div className="mt-1">
          <p className="text-sm font-medium text-aviso">
            Ya tienes a «{mismoOtraEscritura}». Es la misma persona, así que su cuenta se junta
            igual.
          </p>
          <button
            type="button"
            onClick={() => onCambio(mismoOtraEscritura)}
            className="mt-1 min-h-11 w-full rounded-xl border border-marca bg-marca-suave px-3 text-sm font-medium text-marca-fuerte active:bg-superficie"
          >
            Escribirlo como «{mismoOtraEscritura}»
          </button>
        </div>
      )}

      {yaExacto && <p className="mt-1 text-sm font-medium text-exito">✓ {valor.trim()}</p>}

      {esNuevo && <p className="mt-1 text-sm text-texto-suave">Cliente nuevo</p>}

      {paraTocar.length > 0 && mismoOtraEscritura === undefined && (
        <>
          <p className="mt-1 text-xs text-texto-tenue">
            {paraTocar.length === 1 ? 'También tienes a esta persona' : 'También tienes a estas'},
            toca si es quien buscas
          </p>
          <ul className="mt-1 space-y-1">
            {paraTocar.slice(0, maximo).map((cliente) => (
              <li key={cliente}>
                <button
                  type="button"
                  onClick={() => onCambio(cliente)}
                  className="min-h-11 w-full rounded-xl border border-borde bg-superficie px-3 text-left text-sm text-texto active:bg-superficie-suave"
                >
                  {cliente}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Sin nada escrito se ofrecen los mas frecuentes: es lo mas comun. */}
      {buscado === '' && clientes.length > 0 && (
        <>
          <p className="mt-1 text-xs text-texto-tenue">Los que más te piden</p>
          <ul className="mt-1 flex flex-wrap gap-1">
            {clientes.slice(0, maximo).map((cliente) => (
              <li key={cliente}>
                <button
                  type="button"
                  onClick={() => onCambio(cliente)}
                  className="min-h-11 rounded-xl border border-borde bg-superficie px-3 text-sm text-texto active:bg-superficie-suave"
                >
                  {cliente}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
